/**
 * Draft model for the employer case builder. Field names are snake_case to map
 * straight onto the POST /employer/verification-requests/submit/ payload.
 */
export const MAX_PROFILES_PER_CASE = 5;
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

export interface CaseFileMeta {
  s3_key: string;
  file_name: string;
  file_size: number;
  content_type: string;
  uploaded_at: string;
}

export interface CaseEducationEntry {
  id: string;
  degree_name: string;
  school_name: string;
  education_level: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  location_country: string;
  description: string;
}

export interface CaseWorkEntry {
  id: string;
  job_title: string;
  company_name: string;
  industry: string;
  employment_type: string;
  work_mode: string;
  location: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  job_description: string;
}

export interface CaseCertEntry {
  id: string;
  qualification_name: string;
  issuing_organisation: string;
  issue_date: string;
  expiry_date: string;
}

export interface CaseAccountProfile {
  given_name: string;
  family_name: string;
  email: string;
  birth_year: string;
  country_code: string;
  phone_number: string;
  headline: string;
  location_country: string;
  location_city: string;
  bio: string;
}

export interface CaseProfile {
  profile_id: string;
  account_profile: CaseAccountProfile;
  education_entries: CaseEducationEntry[];
  work_entries: CaseWorkEntry[];
  certificate_entries: CaseCertEntry[];
  resume_file: CaseFileMeta | null;
  employee_consent_file: CaseFileMeta | null;
}

export type FileKind = 'employee_resume' | 'employee_consent';

export function emptyProfile(profile_id: string): CaseProfile {
  return {
    profile_id,
    account_profile: {
      given_name: '',
      family_name: '',
      email: '',
      birth_year: '',
      country_code: '+1',
      phone_number: '',
      headline: '',
      location_country: '',
      location_city: '',
      bio: '',
    },
    education_entries: [],
    work_entries: [],
    certificate_entries: [],
    resume_file: null,
    employee_consent_file: null,
  };
}

/** Validation: each profile needs a name and a consent file. */
export function profileIsValid(p: CaseProfile): boolean {
  return (
    p.account_profile.given_name.trim().length > 0 &&
    p.account_profile.family_name.trim().length > 0 &&
    !!p.employee_consent_file
  );
}

/* ---- Submitted case shapes returned by the cases list/detail endpoints ---- */

export interface SubmittedSubtask {
  id: string;
  type: string;
  status: string;
  label?: string;
  reference?: string;
}

export interface SubmittedCase {
  employeeCaseId: number;
  status: string;
  processingStatus: string;
  createdAt: string;
  submittedAt: string;
  updatedAt: string;
  accountProfile: Record<string, unknown> & {
    givenName?: string;
    surname?: string;
    email?: string;
    headline?: string;
  };
  educationEntries: unknown[];
  workEntries: unknown[];
  licenseEntries: unknown[];
  resumeFileMeta: { name?: string; fileUrl?: string | null } | null;
  employeeConsentFileMeta: { name?: string; fileUrl?: string | null } | null;
  subtasks: SubmittedSubtask[];
  outcomeSummary?: string;
}

export interface SubmittedCasesPage {
  count: number;
  page: number;
  page_size: number;
  next_page: number | null;
  previous_page: number | null;
  results: SubmittedCase[];
}
